// src/lib/outbox.js
//
// Durable queue for outbound writes when offline (or when online writes fail).
// Backed by IndexedDB via localCache. Drains automatically on reconnect.
//
// Industry term: outbox pattern. Used in distributed systems to guarantee
// reliable async messaging — write goes to local durable storage first,
// then drains to the remote system when possible.
//
// Operations queued here are kill claims, bounty pursuits, and anything
// else that must reach the server eventually but can wait if signal is down.

import { getDb, STORES } from './idbSchema'

const STORE = STORES.OUTBOX

// Operation handlers are registered by db.js at app startup. Each handler
// knows how to perform one operation type against Supabase. Outbox just
// dispatches to the right handler when draining.
const handlers = new Map()

// Drain state. Prevents concurrent drains from stomping on each other.
let draining = false
let drainTimer = null

// Subscribers notified when queue size changes (for UI sync indicator).
const listeners = new Set()


// --- registration ---
//
// db.js calls register() once per operation type at app startup.

export function register(opName, handler) {
  if (typeof handler !== 'function') {
    throw new Error(`[outbox] handler for "${opName}" must be a function`)
  }
  handlers.set(opName, handler)
}

// --- enqueue ---

export async function enqueue(op, payload) {
  if (!handlers.has(op)) {
    console.warn(`[outbox] enqueueing "${op}" but no handler registered yet`)
  }
  const entry = {
    op,
    payload,
    idempotency_key: payload?.idempotency_key ?? crypto.randomUUID(),
    status: 'pending',
    attempts: 0,
    last_error: null,
    created_at: Date.now(),
  }
  const db = await getDb()
  const id = await db.add(STORE, entry)
  notify()
  // Try to drain immediately if we're online — no point waiting.
  if (navigator.onLine) {
    scheduleDrain(0)
  }
  return id
}

// --- queue inspection (for UI) ---

export async function getPending() {
  const db = await getDb()
  const idx = db.transaction(STORE).store.index('status')
  return idx.getAll('pending')
}

export async function getStuck() {
  const db = await getDb()
  const idx = db.transaction(STORE).store.index('status')
  return idx.getAll('stuck')
}

export async function pendingCount() {
  const items = await getPending()
  return items.length
}

// --- drain ---
//
// Walks pending entries oldest-first, dispatches to the registered
// handler, marks success or failure. Stops cleanly when offline.

export async function drain() {
  if (draining) return
  if (!navigator.onLine) return
  draining = true
  try {
    const pending = await getPending()
    // Oldest first — preserves intent ordering (e.g. claim before pursue).
    pending.sort((a, b) => a.created_at - b.created_at)

    for (const entry of pending) {
      if (!navigator.onLine) break  // signal dropped mid-drain, stop cleanly

      const handler = handlers.get(entry.op)
      if (!handler) {
        await markStuck(entry, `no handler registered for op: ${entry.op}`)
        continue
      }

      try {
        await handler(entry.payload, entry.idempotency_key)
        await remove(entry.id)
      } catch (err) {
        await handleFailure(entry, err)
      }
    }
  } finally {
    draining = false
    notify()
  }
}

async function handleFailure(entry, err) {
  // Distinguish "the network is bad, try again" from "the server rejected
  // this and will keep rejecting." 4xx responses are permanent. 5xx and
  // network errors are transient.
  const isPermanent = err?.status >= 400 && err?.status < 500
  if (isPermanent) {
    await markStuck(entry, err.message || String(err))
    return
  }
  // Transient: bump attempts, leave pending. Next drain retries.
  const db = await getDb()
  const updated = {
    ...entry,
    attempts: entry.attempts + 1,
    last_error: err?.message || String(err),
  }
  await db.put(STORE, updated)
}

async function markStuck(entry, reason) {
  const db = await getDb()
  await db.put(STORE, {
    ...entry,
    status: 'stuck',
    last_error: reason,
  })
}

async function remove(id) {
  const db = await getDb()
  await db.delete(STORE, id)
}

export async function dismissStuck(id) {
  // User saw the stuck-op banner and acknowledged. Remove from queue.
  await remove(id)
  notify()
}

export async function clearAll() {
  const db = await getDb()
  await db.clear(STORE)
  notify()
}

// --- drain scheduling ---
//
// Three triggers:
//   1. Reconnect ('online' event)
//   2. Manual user action ("sync now" button — calls drain() directly)
//   3. Periodic timer (every 30s while online, low-priority background)

function scheduleDrain(delayMs = 0) {
  if (drainTimer) clearTimeout(drainTimer)
  drainTimer = setTimeout(() => {
    drainTimer = null
    drain().catch(err => console.error('[outbox] drain failed', err))
  }, delayMs)
}

// Wire up event listeners and the periodic timer. Called once at app
// startup (db.js handles this).
export function start() {
  window.addEventListener('online', () => scheduleDrain(0))
  setInterval(() => {
    if (navigator.onLine) scheduleDrain(0)
  }, 30_000)
}

// --- subscribers (for sync indicator UI) ---

export function subscribe(fn) {
  listeners.add(fn)
  return () => listeners.delete(fn)  // returns an unsubscribe function
}

function notify() {
  pendingCount().then(count => {
    for (const fn of listeners) {
      try { fn(count) } catch (err) { console.error('[outbox] listener threw', err) }
    }
  })
}

// --- default export ---

export default {
  register,
  enqueue,
  drain,
  start,
  subscribe,
  getPending,
  getStuck,
  pendingCount,
  dismissStuck,
  clearAll,
}